import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../data/store';
import Modal from './common/Modal';
import { styled } from 'styled-components';
import Button from './common/buttons/Button';
import {
	getAvailableGoogleDrives,
	updateCompany,
	verifyGoogleDriveSetup,
	setupCompanyGoogleDrive,
} from '../services/company-service';
import { closeModal } from '../data/stores/modalStore';
import {
	CircularProgress,
	useTheme,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	IconButton,
} from '@mui/material';
import { showAlert } from '../data/stores/alertStore';
import { showModal } from '../data/stores/modalStore';
import { updateCompanySuccess } from '../data/stores/companyStore';
import OnboardingGraphic1 from '../images/Onboarding-1.gif';
import OnboardingGraphic2 from '../images/Onboarding-2.svg';
import OnboardingGraphic3 from '../images/Onboarding-3.svg';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import GradientBackground from '../images/Gradient-Background.svg';
import RadioButtons from './common/buttons/RadioButtons';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Input from './common/form/Input';
import AddIcon from '@mui/icons-material/Add';

interface GoogleDriveSetupResponse {
	[key: string]: {
		exists: boolean;
		message: string;
	};
}

type GoToNextAction = 'verify' | 'setup' | 'finish' | undefined;

interface GoogleDrive {
	id: string;
	name: string;
}

interface StepConfig {
	title: string;
	description: React.ReactNode;
	buttonLabel: string;
	buttonAction: (goToNext: (arg?: GoToNextAction) => void) => void;
	image: string;
	showBusy?: boolean;
	showError?: boolean;
	secondaryButtonLabel?: string;
	secondaryButtonAction?: (goToNext: (arg?: GoToNextAction) => void) => void;
}

export default function GoogleWorkspaceVerificationModalV2() {
	const dispatch = useDispatch<AppDispatch>();
	const { openId } = useSelector((state: RootState) => state.modalState);
	const {
		company: { googleDriveSetupVerified, accountType },
	} = useSelector((state: RootState) => state.companyState);
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedDrive, setSelectedDrive] = useState<string>('');
	const [googleDriveSetupResponse, setGoogleDriveSetupResponse] =
		useState<GoogleDriveSetupResponse | null>(null);

	const [availableDrives, setAvailableDrives] = useState<{
		initialized: boolean;
		sharedDrives: GoogleDrive[];
		myDriveFolders: GoogleDrive[];
	}>({
		initialized: false,
		sharedDrives: [],
		myDriveFolders: [],
	});

	const [showNewDriveInput, setShowNewDriveInput] = useState<boolean>(false);
	const [newDriveName, setNewDriveName] = useState<string>('');
	const fetchAvailableDrives = async () => {
		const response = await getAvailableGoogleDrives(false);
		setAvailableDrives({
			initialized: true,
			sharedDrives: response.sharedDrives,
			myDriveFolders: response.myDriveFolders,
		});
	};

	const stepsConfig: StepConfig[] = [
		{
			title: "Let's Get Your Google Drive Prepped!",
			description: (
				<>
					{accountType === 'workspace'
						? 'First, identify which Shared Drive you want to utilize for your Orbito projects.'
						: 'First, identify which My Drive folder you want to utilize for your Orbito projects.'}
					<Button
						onClick={fetchAvailableDrives}
						variant="text"
						color="primary"
						sx={{
							display: 'flex',
							gap: 1,
							marginTop: '16px',
							fontSize: '14px',
							fontWeight: 'normal',
							textTransform: 'none',
							left: -10,
						}}
					>
						<RefreshOutlinedIcon />{' '}
						{accountType === 'workspace'
							? 'REFRESH SHARED DRIVES'
							: 'REFRESH MY DRIVE FOLDERS'}
					</Button>
					{availableDrives.initialized &&
						((
							accountType === 'workspace'
								? availableDrives.sharedDrives.length > 0
								: availableDrives.myDriveFolders.length > 0
						) ? (
							<DriveSelectionContainer>
								<FormControl component="fieldset">
									<RadioGroup
										value={selectedDrive}
										onChange={(e) =>
											setSelectedDrive(e.target.value)
										}
									>
										{accountType === 'workspace'
											? availableDrives.sharedDrives.map(
													(drive) => (
														<FormControlLabel
															key={drive.id}
															value={drive.id}
															control={<Radio />}
															label={drive.name}
															disabled={
																showNewDriveInput
															}
														/>
													)
											  )
											: availableDrives.myDriveFolders.map(
													(drive) => (
														<FormControlLabel
															key={drive.id}
															value={drive.id}
															control={<Radio />}
															label={drive.name}
															disabled={
																showNewDriveInput
															}
														/>
													)
											  )}
									</RadioGroup>
								</FormControl>
							</DriveSelectionContainer>
						) : (
							<div
								style={{
									marginTop: '16px',
									fontSize: '14px',
								}}
							>
								{accountType === 'workspace'
									? 'No shared drives found. Create a new shared drive below'
									: 'No folders found. Create a new folder below'}
							</div>
						))}
					<Button
						onClick={() => {
							const newShowInput = !showNewDriveInput;
							setShowNewDriveInput(newShowInput);
							if (newShowInput) {
								// Switching to create new mode - deselect existing choice
								setSelectedDrive('');
							} else {
								// Switching to select existing mode - clear new drive name
								setNewDriveName('');
							}
						}}
						variant="text"
						color="primary"
						sx={{
							display: 'flex',
							gap: 1,
							marginTop: '8px',
							fontSize: '14px',
							fontWeight: 'normal',
							textTransform: 'none',
							left: -10,
						}}
					>
						{showNewDriveInput ? (
							<KeyboardArrowUpIcon />
						) : (
							<AddIcon />
						)}
						{showNewDriveInput
							? 'SELECT EXISTING'
							: accountType === 'workspace'
							? 'CREATE NEW SHARED DRIVE'
							: 'CREATE NEW FOLDER'}
					</Button>
					{showNewDriveInput && (
						<Input
							label={
								accountType === 'workspace'
									? 'Shared Drive Name'
									: 'Folder Name'
							}
							style={{ marginTop: '16px' }}
							value={newDriveName}
							onChange={(e) => setNewDriveName(e.target.value)}
							fullWidth
							size="small"
						/>
					)}
				</>
			),
			image: OnboardingGraphic1,
			buttonLabel: 'NEXT',
			buttonAction: (goToNext) => {
				if (!selectedDrive) {
					if (!!newDriveName && newDriveName.trim().length < 4) {
						setError(
							'Enter a name for a new drive that is at least 4 characters long to continue'
						);
						return;
					} else if (!newDriveName) {
						setError('Please select a drive to continue');
						return;
					}
				}
				goToNext();
			},
		},
		{
			title: 'Drive Selected',
			description: (
				<>
					{accountType === 'workspace'
						? 'Drive: ' + newDriveName || selectedDrive
						: 'Folder: ' + newDriveName || selectedDrive}
					<br />
					<br />
					Looks like we have everything we need to complete your
					setup. Click Setup Orbito below and we will build our your
					Orbito folders to get you generating new projects right
					away!
				</>
			),
			image: OnboardingGraphic2,
			buttonLabel: 'SETUP ORBITO',
			buttonAction: async (goToNext) => {
				setBusy(true);
				setError(null);
				try {
					await setupCompanyGoogleDrive(selectedDrive, newDriveName);
					const updatedCompany = await updateCompany({
						googleDriveSetupVerified: true,
						googleDriveSetupVerifiedDate: new Date(),
					});
					dispatch(updateCompanySuccess(updatedCompany));
					goToNext();
				} catch (e: unknown) {
					setError('Error setting up Orbito. Please try again.');
					console.log('setup error', e);
				} finally {
					setBusy(false);
				}
			},
		},
		{
			title: 'ORBITO SET-UP COMPLETE',
			description: (
				<>
					You are now ready to use Orbito, since you are new here
					please take the Orbito tour below. If you want ot skip for
					now you can always take the tour again by clicking the ? in
					the top right corner!
				</>
			),
			image: OnboardingGraphic3,
			buttonLabel: 'TAKE ME TO THE TOUR',
			buttonAction: (goToNext) => {
				dispatch(showModal({ openId: 'tourModal' }));
				// handleClose();
			},
			secondaryButtonLabel: 'SKIP',
			secondaryButtonAction: () => {
				handleClose();
			},
		},
	];

	const errorMessages = useMemo(() => {
		const errors: string[] = [];
		if (googleDriveSetupResponse) {
			Object.entries(googleDriveSetupResponse).forEach(
				([key, item]: [string, any]) => {
					if (!item.exists) {
						errors.push(item.message);
					}
				}
			);
			if (errors.length === 0) {
				updateCompany({
					googleDriveSetupVerified: true,
					googleDriveSetupVerifiedDate: new Date(),
				});
				// setSuccessModalOpen(true);
			} else {
				dispatch(
					showAlert({
						message: 'Error connecting to Google Drive.',
						severity: 'error',
					})
				);
			}
		}
		return errors;
	}, [googleDriveSetupResponse]);

	const open =
		openId === 'googleWorkspaceVerificationModal' ||
		errorMessages.length > 0;

	useEffect(() => {
		if (open && !availableDrives.initialized) {
			fetchAvailableDrives();
		}
	}, [open, fetchAvailableDrives, availableDrives.initialized]);

	const handleClose = () => {
		dispatch(closeModal());
	};

	type GoToNextAction = 'verify' | 'setup' | 'finish' | undefined;
	const goToNext = async (action?: GoToNextAction): Promise<void> => {
		setError(null);
		if (action === 'verify') {
			setBusy(true);
			try {
				const response = await verifyGoogleDriveSetup();
				setGoogleDriveSetupResponse(response);
				const responseValues = Object.values(response) as {
					exists: boolean;
					message: string;
				}[];
				const errors = responseValues.filter((item) => !item.exists);
				if (errors.length > 0) {
					setError(errors.map((e) => e.message).join(' '));
					setBusy(false);
					return;
				}
				// Success, go to next step
				setCurrentStep((prev) => prev + 1);
			} catch (e: unknown) {
				setError('Error connecting to Google Drive.');
			} finally {
				setBusy(false);
			}
			return;
		}
		if (action === 'setup') {
			setBusy(true);
			try {
				setCurrentStep((prev) => prev + 1);
			} catch (e: unknown) {
				setError('Error setting up Orbito.');
			} finally {
				setBusy(false);
			}
			return;
		}
		if (action === 'finish') {
			handleClose();
			return;
		}
		setCurrentStep((prev) => prev + 1);
	};

	const step = stepsConfig[currentStep];

	return (
		<Modal
			open={open}
			handleClose={handleClose}
			sx={{
				padding: 0,
				maxWidth: '95vw',
				overflow: 'hidden',
			}}
			disableBackdropClose={true}
		>
			<StepContainer>
				<StepCard>
					<StepTitle>{step.title}</StepTitle>
					<StepDescription>{step.description}</StepDescription>
					{error && <ErrorText>{error}</ErrorText>}
					<ImageContainer>
						<StepImage src={step.image} alt="Step graphic" />
					</ImageContainer>
					<ButtonContainer>
						{step.secondaryButtonLabel && (
							<Button
								onClick={() =>
									step.secondaryButtonAction?.(goToNext)
								}
								variant="outlined"
								color="primary"
								disabled={busy}
							>
								{step.secondaryButtonLabel}
							</Button>
						)}
						<Button
							onClick={() => step.buttonAction(goToNext)}
							variant="contained"
							color="primary"
							// disabled={busy || !selectedDrive}
							disabled={busy}
						>
							{busy ? (
								<CircularProgress
									size={24}
									sx={{ color: 'white' }}
								/>
							) : (
								step.buttonLabel
							)}
						</Button>
					</ButtonContainer>
				</StepCard>
			</StepContainer>
		</Modal>
	);
}

const StepContainer = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: center;
	background: ${({ theme }) => theme.palette.background.default};
	min-width: 800px;
	max-width: 900px;
	width: 90vw;
	max-height: 90vh;
	box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08);
	border-radius: 32px;
	margin: 0 auto;
	padding: 0;
`;

const StepCard = styled.div`
	background: ${({ theme }) => theme.palette.background.paper};
	border-radius: 32px;
	padding: 48px 56px 32px 56px;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0;
	position: relative;
	max-height: 90vh;
	overflow-y: auto;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: ${({ theme }) => theme.palette.background.default};
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb {
		background: ${({ theme }) => theme.palette.divider};
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: ${({ theme }) => theme.palette.text.disabled};
	}
`;

const StepTitle = styled.h2`
	font-size: 28px;
	font-weight: 700;
	text-align: left;
	margin: 0 0 12px 0;
	color: ${({ theme }) => theme.palette.text.secondary};
`;

const StepDescription = styled.div`
	font-size: 16px;
	text-align: left;
	color: ${({ theme }) => theme.palette.text.primary};
	margin-bottom: 24px;
`;

const ErrorText = styled.div`
	color: ${({ theme }) => theme.palette.error.main};
	font-size: 14px;
	margin-top: 8px;
	line-height: 1.4;
	text-align: center;
`;

const ImageContainer = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background-image: url(${GradientBackground});
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	margin: 40px 0 32px 0;
	border-radius: 16px;
	padding: 16px;
`;

const StepImage = styled.img`
	object-fit: contain;
	width: 100%;
	max-width: 700px;
	height: auto;
	display: block;
	border-radius: 12px;
	box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
	background: #222;
`;

const ButtonContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	width: 100%;
	margin-top: 32px;
	background: ${({ theme }) => theme.palette.background.paper};
`;

const DriveSelectionContainer = styled.div`
	margin-top: 16px;
	width: 100%;
	max-height: 200px;
	overflow-y: auto;
	padding: 8px;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.palette.divider};
`;
